import dayjs from "dayjs";
import { 
  Box,
  ListItem, 
  Chip,
  Stack, 
  Grid, 
  Tooltip,
  Typography,
} from "@mui/material";
import { LockSharp as LockSharpIcon, LockOpenSharp as LockOpenSharpIcon, TaskAlt as TaskAltIcon } from '@mui/icons-material';
import { Label } from "../types/Label";
import { Terminal } from "../types/Terminal";
import { TerminalCall } from "../types/TerminalCall";
import { Vessel } from "../types/Vessel";
import React, { useMemo, useSyncExternalStore } from "react";
import LabelDots from "./partial/LabelDots";

function subscribe(callback) {
  window.addEventListener("resize", callback)
  return () => {
    window.removeEventListener("resize", callback)
  }
}

export function useDimensions(ref) {
  const dimensions = useSyncExternalStore(
    subscribe,
    () => JSON.stringify({
      width: ref.current?.offsetWidth ?? 0,
      height: ref.current?.offsetHeight ?? 0,
    })
  )
  return useMemo(() => JSON.parse(dimensions), [dimensions])
}

export const findObjectById = (objectArray: any, objectId: string) => {
  if (!objectArray || objectArray.length === 0) {
    return undefined;
  }

  return objectArray.find(
    (object: any) => object && object["@id"] && object["@id"] === objectId
  );
};

export const formateDate = (date: Date) => {
  return date ? dayjs(date).format("DD/MM/YYYY") : "";
};

export const colorCodeIsLight = (color: string) => {
  const hex = color.replace('#', '');
  const c_r = parseInt(hex.substring(0, 0 + 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = ((c_r * 299) + (c_g * 587) + (c_b * 114)) / 1000;

  return brightness > 155;
}

export const textColorHandler = (color: string) => {
  if (!color) {
    return '#6C757D';
  }

  return colorCodeIsLight(color) ? '#6C757D' : '#FFFFFF';
}

export const LabelColorFormatter = (color: string | undefined): string => {
  const defaultColor = '#2196f3'

  if (color === undefined) {
    return defaultColor;
  }

  color = color.replace(/\s/g, '')
  color = color.replace(/\#/g, '')
  color = `#${color.toUpperCase()}`

  if (!(/^#([0-9a-f]{3}){1,2}$/i).test(color)) {
    return defaultColor;
  }

  return color;
};

export const formatDateToString = (date: Date | undefined, format = "DD/MM/YYYY") => {
  return date ? dayjs(date).format(format) : "";
};

export const RenderLabelChip = (label: string, color: string) => {
  return <Chip size="small" label={label} sx={{
    backgroundColor: `${color} !important`, color: `${textColorHandler(color)} !important`,
  }} />
}

export const RenderLabelChipNoText = (color: string) => {
  return <Chip size="small" sx={{
    backgroundColor: `${color} !important`, color: `${textColorHandler(color)} !important`
  }} />
}

export const RenderLabels = (labels: Label[], size = 500) => {
  if (labels === undefined || !labels) {
    return <></>;
  }
  
  return (
    <>
      <Stack key={"label-list"}  direction="column" sx={{ padding: 0, minWidth: 0}}>
        {labels.map((label: any) => (
          <ListItem
            sx={{ display: "flex", width: 'auto', padding: '4px' }}
            key={label["@id"]}
          >
            {size < 400 ? RenderLabelChipNoText(label.color) : RenderLabelChip(label.name, label.color)}
          </ListItem>
        ))}
      </Stack>
    </>
  );
}

export const RenderLegendList = ({title, list}) => {
  if (list === undefined || !list) {
    return <></>;
  }
  
  return (
    <>
    { title ?? (<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>) }
    <Stack key={"label-legend"}  direction="column" sx={{ padding: 0, minWidth: 0}}>
      {list.map((item: any, index: number) => (
        <ListItem
          key={`${index}-${item.label}`}
          sx={{
            padding: 0,
          }}
        >
          <Grid container gap={1} flexWrap="nowrap" alignItems="center">
            <Box sx={{
              height: "15px",
              width: "15px",
              backgroundColor: LabelColorFormatter(item.color),
            }}></Box>
            <Typography>{item.label ?? item.name}</Typography>
          </Grid>
        </ListItem>
      ))}
    </Stack>
    </>
  );
}

export const RenderTerminalName = (terminal: Terminal, showLabels: boolean = false) => {
  return (
    <>
      <Grid
        item
        sx={{
          height: "15px",
          width: "5px !important",
          backgroundColor: terminal.color ?? "#FFFFF",
          border: "1px solid #DDDDDD",
        }}
      ></Grid>
      &nbsp;{terminal && terminal.name ? terminal.name : ''}
      <LabelDots labels={terminal.labels}/>
    </>
  )
}

export const terminalCallFormatter = (terminalCalls: TerminalCall[]) => {
  const formattedTerminalCallList = [];

  if (!terminalCalls) {
    return formattedTerminalCallList;
  }

  terminalCalls.sort(function (
    a: any,
    b: any
  ) {
    return Number(new Date(a.planned)) - Number(new Date(b.planned));
  });

  terminalCalls.map((termincalCall: TerminalCall) => {
    formattedTerminalCallList[
      formatDateToString(termincalCall.planned)
    ] ??= [];
    formattedTerminalCallList[
      formatDateToString(termincalCall.planned)
    ].push(termincalCall);
  });

  return formattedTerminalCallList;
}

export const containerUidFormatter = (uid) => {
  const letters = uid.substring(0, 4)
  const numbers = uid.substring(4, uid.length)
  return {
    letters: letters,
    numbers: numbers,
  }
}

export const RenderTerminalCallLock = (terminalCall: TerminalCall) => {
  const completed = terminalCall?.departure && terminalCall.status === 'Completed';

  return (
    <>
      {completed ? (
        <TaskAltIcon fontSize="inherit" color="success" />
      ) : terminalCall.loadingPlanLocked ? (
        <Tooltip title="The captain has approved the stowage plan.">
          <LockSharpIcon fontSize="inherit" sx={{ color: '#666666' }} />
        </Tooltip>
      ) : (
        <LockOpenSharpIcon fontSize="inherit" sx={{ color: '#666666' }} />
      )}&nbsp;
    </>
  )
}

export function getTerminalCallArrivalStateContainerIds(
  terminalCall?: TerminalCall
): string[] {
  if (!terminalCall) {
    return [];
  }

  const positions = [
    ...(terminalCall.arrivalState?.container_positions ?? []),
    ...(terminalCall.bargearrivalState?.container_positions ?? []),
  ];

  const uris = positions
    .map(([containerId]) => `/api/containers/${containerId}`)
    .filter((uri): uri is string => Boolean(uri));

  return Array.from(new Set(uris));
}

export const getTerminalCallLoadingContainerIds = (
  terminalCall?: TerminalCall | null
): any[] => {
  if (!terminalCall) {
    return [];
  }

  const arrivalContainerIds = getTerminalCallArrivalStateContainerIds(terminalCall);

  const unloadingContainerIds = Array.isArray((terminalCall as any).unloadingContainers)
    ? (terminalCall as any).unloadingContainers
        .map((container: any) => {
          if (typeof container === "string") {
            return container;
          }

          return container?.["@id"] ?? null;
        })
        .filter(Boolean)
    : [];

  const loadingContainers = Array.isArray((terminalCall as any).loadingContainers)
    ? (terminalCall as any).loadingContainers
    : [];

  const filteredArrivalContainerIds = arrivalContainerIds.filter(
    (containerId: string) => !unloadingContainerIds.includes(containerId)
  );

  return [...filteredArrivalContainerIds, ...loadingContainers];
};

export const hasPositionBarrier = (axis: any, vessel: Vessel) => {
  let requirementsObtained: any = [];

  if (!vessel || vessel.barriers.length === 0) {
    return false;
  }

  vessel.barriers.map((barrierPosition: any) => {
    requirementsObtained.push(JSON.stringify(barrierPosition) === JSON.stringify(axis))
  })

  return requirementsObtained.includes(true);
}

export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

export const stringAvatar = (name: string) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}

const formatDateKey = (date?: Date) => {
  if (!date) return "";
  const day = dayjs(date);
  const isToday = day.isSame(dayjs(), "day");

  return `${day.format("DD/MM/YYYY")}${isToday ? " (Today)" : ""}`;
};

export const groupTerminalCalls = (calls: TerminalCall[]): Record<string, TerminalCall[]> => {
  return calls
    .slice()
    .sort((a, b) => {
      const timeA = new Date(a.planned ?? a.arrival).getTime();
      const timeB = new Date(b.planned ?? b.arrival).getTime();
      return timeA - timeB;
    })
    .reduce<Record<string, TerminalCall[]>>((acc, call) => {
      const key = formatDateKey(call.planned ?? call.arrival);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(call);
      return acc;
    }, {});
};